import type { EnrichedParticipants } from '@ps/isp-web-services-types';
import { Translation } from '@ps/web-shared-components';
import { unzip } from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '@swf/core/store';
import { Selection } from '@swf/sports/components';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import type {
  MarketTemplateComponent,
  SingleMarketTemplateProps
} from '@swf/sports/types/templates';

import useMarketDisplayLimit from '../../hooks/useMarketDisplayLimit';

import { TXTWCL_Show_More } from './AlternateHandicap.messages.json';
import styles from './AlternateHandicap.scss';
import { groupRunners, runnerNameMatchPattern } from './utils/group-runners';

const SHOW_MORE_THRESHOLD = 10;

const useColumnTitles = (participants: EnrichedParticipants | undefined): string[] => [
  participants?.[0]?.participant ?? '',
  participants?.[1]?.participant ?? ''
];

const AlternateHandicap: MarketTemplateComponent<SingleMarketTemplateProps> = ({ marketId }) => {
  const { widgetSelect } = useEventMarketTemplatesWidget();
  const market = useSelector((state: RootState) => widgetSelect.market(state, marketId));
  const { enrichedParticipants, eventStartTime } = useSelector((state: RootState) =>
    widgetSelect.event(state, market?.eventId ?? -1)
  );

  const headerRow = useColumnTitles(enrichedParticipants);

  const [homeRunners, awayRunners] = useMemo(
    () => groupRunners(market?.runners, enrichedParticipants),
    [enrichedParticipants, market?.runners]
  );

  const rows = useMemo(() => unzip([homeRunners, awayRunners]), [homeRunners, awayRunners]);

  const [rowsToDisplay, handleShowMoreClick, limitRows] = useMarketDisplayLimit(
    rows,
    marketId,
    SHOW_MORE_THRESHOLD
  );

  if (!enrichedParticipants || !market) return null;

  return (
    <>
      <table data-testid="market-template-alternate-handicap" className={styles.table}>
        <thead>
          <tr>
            {headerRow.map((header) => (
              <th scope="col" key={`header-${market.marketId}-${header}`}>
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowsToDisplay.map((row, i) => (
            <tr key={`row-${market.marketId}-${i}`}>
              {row.map((runner, j) => {
                if (typeof runner === 'string') {
                  return (
                    <td key={`cell-${market.marketId}-${i}-${j}`}>
                      <span className={styles.emptySelection}>-</span>
                    </td>
                  );
                }

                const selectionText =
                  runner.runnerName.match(runnerNameMatchPattern)?.[1] ?? undefined;

                return (
                  <td key={runner.selectionId}>
                    {
                      <Selection
                        competitionId={market.competitionId}
                        eventId={market.eventId}
                        eventTypeId={market.eventTypeId}
                        handicap={runner.handicap}
                        legTypes={market.legTypes}
                        marketId={market.marketId}
                        marketStatus={market.marketStatus}
                        marketType={market.marketType}
                        odds={runner.winRunnerOdds}
                        runnerResultType={runner.resultType}
                        runnerStatus={runner.runnerStatus}
                        selectionId={runner.selectionId}
                        selectionText={selectionText}
                        startTime={eventStartTime}
                        isMultiline
                      />
                    }
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {limitRows && (
        <div className={styles.showMoreWrapper}>
          <button type="button" className={styles.showMore} onClick={handleShowMoreClick}>
            <Translation {...TXTWCL_Show_More} />
          </button>
        </div>
      )}
    </>
  );
};

AlternateHandicap.marketTemplateType = 'EVENT';

export default AlternateHandicap;
